<template>
  <div class="institution-details-grid two-columns">
    <div class="institution-details-grid-column">
      <BaseBox display="flex" flex-direction="column" gap="lg">
        <BaseCard>
          <BaseCardBody>
            <BaseBox
              display="flex"
              :flex-direction="{ '': 'column', sm: 'row' }"
              align-items="center"
              gap="md"
            >
              <BaseAvatar
                :src="institution.photo"
                :text="getInitials(institution.name)"
                size="xxl"
              />
              <div>
                <h4 class="institution-details-name">
                  {{ institution.name }}
                </h4>
                <p class="institution-details-description">
                  {{ institution.description }}
                </p>
              </div>
            </BaseBox>
          </BaseCardBody>
        </BaseCard>

        <BaseCard>
          <BaseCardGroup>
            <template #title>
              <BaseBox display="flex" align-items="center" gap="sm">
                <BaseIcon icon="pin" size="sm" />
                Endereço
              </BaseBox>
            </template>
          </BaseCardGroup>

          <BaseCardBody>
            <span
              class="institution-details-address"
              v-for="(unit, idx) in institution.units"
              :key="unit.id + idx"
            >
              {{ unit.name }}
              <p>{{ unitAddress(unit.address) }}</p>
            </span>
          </BaseCardBody>
        </BaseCard>
      </BaseBox>
    </div>

    <div class="institution-details-grid-column">
      <BaseCard>
        <BaseCardGroup>
          <h2 class="institution-details-schedule">
            Marque sua consulta online
          </h2>
        </BaseCardGroup>

        <BaseCardGroup>
          <form>
            <BaseSelect
              class="mb-4"
              label="Unidade de atendimento"
              size="xl"
              :options="unitsOptions"
              v-model="form.unit"
            />
            <BaseSelect
              label="Especialidade"
              size="xl"
              :options="specialtiesOptions"
              v-model="form.specialty"
              :disabled="isSelectDisabled"
            />
          </form>
        </BaseCardGroup>

        <BaseCardGroup>
          <BaseBox
            display="flex"
            :flex-direction="{ '': 'column', sm: 'row' }"
            :justify-content="{ sm: 'between' }"
            gap="md"
          >
            <template v-if="!doctor">
              <BaseBox display="flex" align-items="center">
                <p class="institution-details-doctor-chose">
                  Escolha o profissional de saúde
                </p>
              </BaseBox>
              <BaseButton
                variant="neutral"
                pill
                :disabled="isDoctorSelectButtonDisabled"
                @click="showDoctorsModal"
              >
                <BaseIcon
                  icon="plus"
                  size="xs"
                  style="width: 12px; height: 12px"
                />
                Adicionar
              </BaseButton>
            </template>
            <template v-else>
              <BaseBox
                display="flex"
                :flex-direction="{ '': 'column', sm: 'row' }"
                align-items="center"
                gap="md"
              >
                <BaseAvatar
                  :src="doctor.photo"
                  :text="getInitials(doctor.name)"
                  size="md"
                />
                <div>
                  <h4 class="institution-details-doctor">
                    {{ doctor.name }}
                  </h4>
                  <p class="institution-details-doctor-crm">
                    {{ doctor.doctor_property.council_name }} -
                    {{ doctor.doctor_property.council_number }}
                  </p>
                </div>
              </BaseBox>
              <BaseBox display="flex" align-items="center">
                <BaseButton variant="neutral" pill block @click="showDoctorsModal">
                  Alterar
                </BaseButton>
              </BaseBox>
            </template>
          </BaseBox>
        </BaseCardGroup>

        <template v-if="doctor">
          <BaseCardGroup>
            <ScheduleTable
              :entity="doctor"
              :unit-id="unitId"
              only-select-slot
              @on-select-slot="onSelectDctorSlot"
            />
          </BaseCardGroup>
        </template>

        <BaseCardGroup>
          <BaseButton size="xl" block :disabled="!selected_slot" @click="scheduleSlot">
            Agendar Consulta
          </BaseButton>
        </BaseCardGroup>
      </BaseCard>
    </div>

    <BModal
      id="doctorsModal"
      ref="doctorsModal"
      size="xl"
      centered
      hide-footer
      hide-header
    >
      <InstitutionDetailsModal
        :unit-id="institutionUnitId"
        :specialty-id="institutionSpecialtyId"
        @chooseDoctor="chooseScheduleDoctor"
      />
    </BModal>
    <ScheduleComfirmationModal />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { getNameInitials } from '../../utils/getNameInitials';
import InstitutionDetailsModal from './InstitutionDetailsModal.vue';
import ScheduleTable from '../../components/Schedule/ScheduleTable.vue';
import ScheduleComfirmationModal from '../Schedule/ScheduleComfirmationModal.vue';

export default {
  name: 'InstitutionDetails',
  components: {
    InstitutionDetailsModal,
    ScheduleTable,
    ScheduleComfirmationModal,
  },
  data() {
    return {
      isLoadingSlots: false,
      error: null,
      doctor: null,
      slots: [],
      specialties: [],
      selected_slot: null,
      form: {
        unit: '',
        specialty: '',
      },
    };
  },
  methods: {
    ...mapActions('institutions', ['fetchInstitutionUnitSpecialties']),
    ...mapActions('doctor', ['fetchDoctorsAvailables']),
    ...mapActions('appointments', ['holdScheduleAppointment']),

    async getInstitutionUnitSpecialties() {
      const response = await this.fetchInstitutionUnitSpecialties(
        this.institutionUnitId,
      );
      this.specialties = response.data;
    },

    async getDoctorAvailableSlots() {
      try {
        this.isLoadingSlots = true;
        const response = await this.fetchDoctorsAvailables({ id: this.doctor.id });
        this.slots = response.data.filter((availableSlot) => availableSlot.id);
      } catch (err) {
        this.error = err;
      } finally {
        this.isLoadingSlots = false;
      }
    },
    onSelectDctorSlot(slot) {
      this.selected_slot = slot;
    },
    scheduleSlot() {
      let hour = this.selected_slot.hour;
      let date = this.selected_slot.slot_date;
      let id = this.selected_slot.slot_id;
      this.holdScheduleAppointment({
        scheduled_at: `${date} ${hour}:00`,
        scheduled_until: `${date} 00:00:00`,
        doctor: this.doctor,
        slot_date: date,
        slot_hour: hour,
        slot_id: id,
        institution_id: this.institution.id,
        institution_type: this.institutionType,
      });
      if (!this.loggedIn) {
        this.$bvModal.show('signinModal');
      } else {
        this.$router.push({ name: 'Schedule' });
      }
    },
    getInitials(name) {
      return getNameInitials(name);
    },
    unitAddress(address) {
      const { street, street_number, neighborhood, city, state, zip_code } = address;
      return `${street}, ${street_number}, ${neighborhood}, ${city} - ${state}, CEP: ${zip_code}.`;
    },
    chooseScheduleDoctor(doctor) {
      this.doctor = doctor;
      this.hideDoctorsModal();
    },
    showDoctorsModal() {
      this.$bvModal.show('doctorsModal');
    },
    hideDoctorsModal() {
      this.$bvModal.hide('doctorsModal');
    },
  },
  computed: {
    ...mapGetters('institutions', [
      'institution',
      'unitsOptions',
      'isLoadingSpecialties',
    ]),
    ...mapGetters('auth', ['loggedIn']),

    institutionId() {
      return this.$route.params.id;
    },
    institutionUnitId() {
      return this.form.unit;
    },
    institutionSpecialtyId() {
      return this.form.specialty;
    },
    specialtiesOptions() {
      let options = [{ label: 'Escolha', value: null }];
      return options.concat(
        this.specialties.map((specialty) => ({
          label: specialty.name,
          value: specialty.id,
        })),
      );
    },
    isSelectDisabled() {
      return !this.specialties.length || this.isLoadingSpecialties;
    },
    isDoctorSelectButtonDisabled() {
      return !this.form.specialty;
    },
    unitId() {
      let unit_id = this.unitsOptions.filter(unit => this.institutionUnitId === unit.value)[0].value
      return unit_id
    },
    institutionType() {
      return new URLSearchParams(window.location.search).get('institution_type')
    }
  },
  watch: {
    institutionUnitId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.getInstitutionUnitSpecialties();
      }
    },
    doctor(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.unitId()
        this.getDoctorAvailableSlots();
      }
    },
  },
};
</script>
